import { css, cx } from "styled-system/css";
import { useEffect, useState } from "react";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useAuth } from "@/providers/AuthProvider";
import { cf } from "@/components/brand/sfturnos-styles";

type Ticket = {
  id: string;
  status: "PENDING" | "IN_REVIEW" | "FIXED" | "DELAYED";
  clientName: string;
  clientEmail: string;
  clientPhone: string | null;
  message: string;
  createdAt: string;
  updatedAt: string;
};

function statusLabel(s: Ticket["status"]) {
  if (s === "PENDING") return "Pendiente";
  if (s === "IN_REVIEW") return "En revisión";
  if (s === "DELAYED") return "Demorado";
  return "Arreglado";
}

export function DashboardSupport() {
  const { refresh } = useAuth();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [tickets, setTickets] = useState<Ticket[] | null>(null);

  async function load() {
    const r = await fetchWithSessionSync("/api/dashboard/support-tickets", { credentials: "include" }, refresh);
    if (!r.ok) return;
    setTickets((await r.json()) as Ticket[]);
  }

  useEffect(() => {
    void load();
  }, [refresh]);

  async function submit() {
    if (message.trim().length < 10) return;
    setSending(true);
    const r = await fetchWithSessionSync(
      "/api/dashboard/support-tickets",
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message,
        }),
      },
      refresh,
    );
    setSending(false);
    if (!r.ok) return;
    const created = (await r.json()) as Ticket;
    setMessage("");
    setTickets((prev) => (prev ? [created, ...prev] : [created]));
  }

  return (
    <DashboardPageShell
      eyebrow="Soporte"
      title="Ayuda"
      description="Reportá problemas de tu cuenta de SFTurnos. Estados: Pendiente, En revisión, Arreglado, Demorado."
    >
      <div className={css({ display: "grid", gap: "4", maxW: "42rem" })}>
        <div className={cx(cf.dashSurface, css({ p: "5", display: "grid", gap: "3" }))}>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Describí el problema..."
            className={cx(cf.input, css({ minH: "120px", resize: "vertical" }))}
          />
          <button
            type="button"
            disabled={sending || message.trim().length < 10}
            onClick={() => void submit()}
            className={css({
              px: "4",
              py: "2.5",
              rounded: "lg",
              fontSize: "sm",
              fontWeight: "semibold",
              color: "white",
              border: "none",
              cursor: sending ? "wait" : "pointer",
              bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
              opacity: sending || message.trim().length < 10 ? 0.6 : 1,
            })}
          >
            {sending ? "Enviando..." : "Enviar ticket"}
          </button>
        </div>

        <div className={css({ display: "grid", gap: "3" })}>
          {(tickets ?? []).map((t) => (
            <div key={t.id} className={cx(cf.dashSurface, css({ p: "4" }))}>
              <div className={css({ display: "flex", justifyContent: "space-between", gap: "2", flexWrap: "wrap" })}>
                <span className={css({ fontSize: "xs", color: "#64748b" })}>{new Date(t.createdAt).toLocaleString("es-AR")}</span>
                <span className={css({ fontSize: "xs", fontWeight: "semibold", color: "#334155" })}>
                  Estado: {statusLabel(t.status)}
                </span>
              </div>
              <p className={css({ mt: "2", fontSize: "sm", color: "#0f172a", whiteSpace: "pre-wrap" })}>{t.message}</p>
            </div>
          ))}
          {tickets && tickets.length === 0 ? (
            <p className={css({ fontSize: "sm", color: "#64748b" })}>Todavía no enviaste tickets.</p>
          ) : null}
        </div>
      </div>
    </DashboardPageShell>
  );
}

